import React, { forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { propsUtil, hashUtil } from '@asphalt-react/helper';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Checkbox__M_BW5 {\n  position: absolute; /* take it out of document flow */\n  opacity: 0; /* hide it */\n  width: 0;\n}\n\n.checkboxWrap__GRd_l {\n  box-sizing: border-box;\n  display: inline-flex;\n  align-items: center;\n  position: relative;\n}\n\n.customLabel__3RXQh {\n  /* default custom properties */\n  --checkbox-border-radius: var(--roundness-1, 2px);\n  --checkbox-border-default: var(\n    --interactive-border-solid,\n    solid hsla(220, 22%, 76%, 1)\n  );\n  --checkbox-border-color-focus: var(\n    --interactive-focus,\n    hsla(219, 42%, 18%, 1)\n  );\n  --checkbox-border-color-active: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n  --checkbox-background-color: var(\n    --interactive-fill-default,\n    hsla(0, 0%, 100%, 1)\n  );\n  --checkbox-background-color-active: var(\n    --interactive-fill-selected-active,\n    hsla(219, 42%, 24%, 1)\n  );\n  --checkbox-background-color-hover: var(\n    --interactive-fill-hover,\n    hsla(216, 24%, 96%, 1)\n  );\n  --checkbox-background-selected-hover-color: var(\n    --interactive-fill-selected-hover,\n    hsla(219, 23%, 44%, 1)\n  );\n  --checkbox-border-color-error: var(\n    --interactive-border-support,\n    hsla(12, 92%, 36%, 1)\n  );\n  --checkbox-disabled-color: var(--interactive-disabled, hsla(0, 0%, 86%, 1));\n  --checkbox-font-size-l: var(--body-l, 1.13rem);\n  --checkbox-font-size-m: var(--body-m, 1rem);\n  --checkbox-font-size-s: var(--body-s, 0.88rem);\n  --checkbox-font-color: var(--text-secondary, hsla(219, 42%, 24%, 1));\n  --checkbox-font-color-disabled: var(--text-disabled, hsla(0, 0%, 62%, 1));\n  --checkbox-tick-color: var(--icon-inverse, hsla(0, 0%, 100%, 1));\n\n  /* box shadows for ticks */\n\n  /* small tick */\n  --checkbox-tick-small: 0.0625rem 0 0 var(--checkbox-tick-color),\n    0.125rem 0 0 var(--checkbox-tick-color),\n    0.1875rem 0 0 var(--checkbox-tick-color),\n    0.1875rem -0.0625rem 0 var(--checkbox-tick-color),\n    0.1875rem -0.125rem 0 var(--checkbox-tick-color),\n    0.1875rem -0.1875rem 0 var(--checkbox-tick-color),\n    0.1875rem -0.25rem 0 var(--checkbox-tick-color),\n    0.1875rem -0.3125rem 0 var(--checkbox-tick-color),\n    0.1875rem -0.375rem 0 var(--checkbox-tick-color);\n\n  /* medium tick */\n  --checkbox-tick-medium: 0.0625rem 0 0 var(--checkbox-tick-color),\n    0.125rem 0 0 var(--checkbox-tick-color),\n    0.1875rem 0 0 var(--checkbox-tick-color),\n    0.25rem 0 0 var(--checkbox-tick-color),\n    0.25rem -0.0625rem 0 var(--checkbox-tick-color),\n    0.25rem -0.125rem 0 var(--checkbox-tick-color),\n    0.25rem -0.1875rem 0 var(--checkbox-tick-color),\n    0.25rem -0.25rem 0 var(--checkbox-tick-color),\n    0.25rem -0.3125rem 0 var(--checkbox-tick-color),\n    0.25rem -0.375rem 0 var(--checkbox-tick-color),\n    0.25rem -0.4375rem 0 var(--checkbox-tick-color),\n    0.25rem -0.5rem 0 var(--checkbox-tick-color);\n\n  /* large tick */\n  --checkbox-tick-large: 0.0625rem 0 0 var(--checkbox-tick-color),\n    0.125rem 0 0 var(--checkbox-tick-color),\n    0.1875rem 0 0 var(--checkbox-tick-color),\n    0.25rem 0 0 var(--checkbox-tick-color),\n    0.3rem 0 0 var(--checkbox-tick-color),\n    0.35rem 0 0 var(--checkbox-tick-color),\n    0.35rem -0.0625rem 0 var(--checkbox-tick-color),\n    0.35rem -0.125rem 0 var(--checkbox-tick-color),\n    0.35rem -0.1875rem 0 var(--checkbox-tick-color),\n    0.35rem -0.25rem 0 var(--checkbox-tick-color),\n    0.35rem -0.3125rem 0 var(--checkbox-tick-color),\n    0.35rem -0.375rem 0 var(--checkbox-tick-color),\n    0.35rem -0.4375rem 0 var(--checkbox-tick-color),\n    0.35rem -0.5rem 0 var(--checkbox-tick-color),\n    0.35rem -0.5625rem 0 var(--checkbox-tick-color),\n    0.35rem -0.625rem 0 var(--checkbox-tick-color),\n    0.35rem -0.6875rem 0 var(--checkbox-tick-color);\n\n  padding: 0;\n  display: inline-flex;\n  align-items: center;\n  color: var(--checkbox-font-color);\n  line-height: initial;\n  box-sizing: border-box;\n}\n\n.customLabel__3RXQh > span {\n  line-height: initial;\n  margin-left: 0.75rem;\n}\n\n/* handle rtl direction */\n*[dir=\"rtl\"] .customLabel__3RXQh > span {\n  margin-right: 0.75rem;\n  margin-left: 0;\n}\n\n*[dir=\"rtl\"] .Checkbox__M_BW5:checked + .s__1ppEb:after {\n  right: 0.55rem;\n}\n\n*[dir=\"rtl\"] .Checkbox__M_BW5:checked + .m__15LW0:after {\n  right: 0.65rem;\n}\n\n*[dir=\"rtl\"] .Checkbox__M_BW5:checked + .l__2b2kJ:after {\n  right: 0.9rem;\n}\n\n*[dir=\"rtl\"] .Checkbox__M_BW5:indeterminate + .s__1ppEb:after,\n*[dir=\"rtl\"] .Checkbox__M_BW5:indeterminate + .m__15LW0:after {\n  right: 0.25rem;\n}\n\n*[dir=\"rtl\"] .Checkbox__M_BW5:indeterminate + .l__2b2kJ:after {\n  right: 0.3rem;\n}\n\n.s__1ppEb {\n  font-size: var(--checkbox-font-size-s);\n}\n\n.m__15LW0 {\n  font-size: var(--checkbox-font-size-m);\n}\n\n.l__2b2kJ {\n  font-size: var(--checkbox-font-size-l);\n}\n\n/* Styles for box */\n.customLabel__3RXQh:before {\n  content: \"\";\n  display: inline-block;\n  vertical-align: middle;\n  background-color: var(--checkbox-background-color);\n  border: var(--border-width, 1px) var(--checkbox-border-default);\n  border-radius: var(--checkbox-border-radius);\n  box-sizing: inherit;\n}\n\n.s__1ppEb:before {\n  width: 0.875rem;\n  height: 0.875rem;\n}\n\n.m__15LW0:before {\n  width: 1rem;\n  height: 1rem;\n}\n\n.l__2b2kJ:before {\n  width: 1.25rem;\n  height: 1.25rem;\n}\n\n/* Box hover */\n.Checkbox__M_BW5:hover + .customLabel__3RXQh:before {\n  background-color: var(--checkbox-background-color-hover);\n}\n\n/* Box focus */\n.Checkbox__M_BW5:focus + .customLabel__3RXQh:before {\n  background-color: var(--checkbox-background-color-hover);\n  border-color: var(--checkbox-border-color-focus);\n}\n\n/* Box checked */\n.Checkbox__M_BW5:checked + .customLabel__3RXQh:before,\n.Checkbox__M_BW5:indeterminate + .customLabel__3RXQh::before {\n  background-color: var(--checkbox-background-color-active);\n  border-color: var(--checkbox-border-color-active);\n}\n\n.Checkbox__M_BW5:checked:hover + .customLabel__3RXQh:before,\n.Checkbox__M_BW5:indeterminate:hover + .customLabel__3RXQh::before,\n.Checkbox__M_BW5:checked:focus + .customLabel__3RXQh:before,\n.Checkbox__M_BW5:indeterminate:focus + .customLabel__3RXQh::before {\n  background-color: var(--checkbox-background-selected-hover-color);\n  border-color: var(--checkbox-background-selected-hover-color);\n}\n\n/* Disabled state label */\n.Checkbox__M_BW5:disabled + .customLabel__3RXQh {\n  color: var(--checkbox-font-color-disabled);\n  cursor: not-allowed;\n}\n\n/* Disabled box. */\n.Checkbox__M_BW5:disabled + .customLabel__3RXQh:before {\n  background-color: var(--checkbox-disabled-color);\n  border-color: var(--checkbox-disabled-color);\n  cursor: not-allowed;\n}\n\n/* Invalid state box */\n.Checkbox__M_BW5:invalid + .customLabel__3RXQh:before {\n  border-color: var(--checkbox-border-color-error);\n}\n\n/* styles for tick mark */\n.Checkbox__M_BW5:checked + .customLabel__3RXQh:after {\n  content: \"\";\n  position: absolute;\n  width: 0.125rem;\n  height: 0.125rem;\n  background-color: var(--checkbox-tick-color);\n  transform: rotate(45deg);\n  border-radius: 3px;\n  box-sizing: inherit;\n}\n\n/* tick for small checkbox */\n.Checkbox__M_BW5:checked + .s__1ppEb:after {\n  left: 0.1875rem;\n  box-shadow: var(--checkbox-tick-small);\n}\n\n/* tick for medium checkbox */\n.Checkbox__M_BW5:checked + .m__15LW0:after {\n  left: 0.1875rem;\n  box-shadow: var(--checkbox-tick-medium);\n}\n\n/* tick for large checkbox */\n.Checkbox__M_BW5:checked + .l__2b2kJ:after {\n  left: 0.18rem;\n  box-shadow: var(--checkbox-tick-large);\n}\n\n/* Layout for inderminate state icon */\n.Checkbox__M_BW5:indeterminate + .customLabel__3RXQh:after {\n  content: \"\";\n  position: absolute;\n  height: 0.125rem;\n  background-color: var(--checkbox-tick-color);\n  box-sizing: inherit;\n}\n\n/* indeterminate state for small checkbox */\n.Checkbox__M_BW5:indeterminate + .s__1ppEb:after {\n  width: 0.375rem;\n  left: 0.25rem;\n}\n\n/* indeterminate state for small checkbox */\n.Checkbox__M_BW5:indeterminate + .m__15LW0:after {\n  width: 0.5rem;\n  left: 0.25rem;\n}\n\n/* indeterminate state for small checkbox */\n.Checkbox__M_BW5:indeterminate + .l__2b2kJ:after {\n  width: 0.625rem;\n  left: 0.3125rem;\n}\n\n/* Styles for pointing devices */\n@media (pointer: coarse) {\n  .customLabel__3RXQh {\n    padding: 0.625rem 0.625rem 0.625rem 0;\n  }\n  .s__1ppEb:before,\n  .m__15LW0:before,\n  .l__2b2kJ:before {\n    min-width: 1.25rem;\n    min-height: 1.25rem;\n  }\n\n  /* styles for tick */\n  .Checkbox__M_BW5:checked + .l__2b2kJ:after,\n  .Checkbox__M_BW5:checked + .m__15LW0:after,\n  .Checkbox__M_BW5:checked + .s__1ppEb:after {\n    left: 0.25rem;\n    box-shadow: var(--checkbox-tick-large);\n  }\n\n  .Checkbox__M_BW5.hideLabel__28qlD:checked + .s__1ppEb:after,\n  .Checkbox__M_BW5.hideLabel__28qlD:checked + .m__15LW0:after,\n  .Checkbox__M_BW5.hideLabel__28qlD:checked + .l__2b2kJ:after {\n    left: 0.2rem;\n    box-shadow: var(--checkbox-tick-large);\n  }\n\n  /* styles for indeterminate state */\n  .Checkbox__M_BW5:indeterminate + .l__2b2kJ:after,\n  .Checkbox__M_BW5:indeterminate + .m__15LW0:after,\n  .Checkbox__M_BW5:indeterminate + .s__1ppEb:after,\n  .Checkbox__M_BW5.hideLabel__28qlD:indeterminate + .s__1ppEb:after,\n  .Checkbox__M_BW5.hideLabel__28qlD:indeterminate + .m__15LW0:after,\n  .Checkbox__M_BW5.hideLabel__28qlD:indeterminate + .l__2b2kJ:after {\n    min-width: 0.625rem;\n    left: 0.3125rem;\n  }\n\n  .l__2b2kJ,\n  .m__15LW0,\n  .s__1ppEb {\n    font-size: var(--checkbox-font-size-l);\n  }\n}\n";
var styles = {"Checkbox":"Checkbox__M_BW5","checkboxWrap":"checkboxWrap__GRd_l","customLabel":"customLabel__3RXQh","s":"s__1ppEb","m":"m__15LW0","l":"l__2b2kJ","hideLabel":"hideLabel__28qlD"};
styleInject(css_248z);

var sizeClass = function sizeClass(size) {
  switch (size) {
    case "l":
      return styles.l;

    case "m":
      return styles.m;

    case "s":
      return styles.s;

    default:
      return styles.m;
  }
};
/**
 * Create css classes from props
 *
 * @param {object} param custom props object
 * @returns {string}
 */


var createFieldClasses = function createFieldClasses(size) {
  return cn(styles.customLabel, sizeClass(size));
};

/**
 * size values
 */
var sizes = ["s", "m", "l"];

var Checkbox = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var size = _ref.size,
      label = _ref.label,
      indeterminate = _ref.indeterminate,
      id = _ref.id,
      style = _ref.style,
      rest = _objectWithoutProperties(_ref, ["size", "label", "indeterminate", "id", "style"]);

  if (!propsUtil.validateSize(sizes, size)) {
    size = "m";
    console.warn("size should be one of [".concat(sizes, "]. Using fallback size '").concat(size, "'"));
  }

  var hideLabel = !label;
  var checkboxClass = cn(styles.Checkbox, _defineProperty({}, styles.hideLabel, hideLabel));
  var checkboxID = id || hashUtil.randomHash(); // If the user gives an ID, the use that otherwise generate a new one

  var labelClass = createFieldClasses(size);
  var checkboxRef = ref || /*#__PURE__*/React.createRef(); // create ref if ref is not passed

  useEffect(function () {
    checkboxRef.current.indeterminate = indeterminate;
  });
  return /*#__PURE__*/React.createElement("span", {
    className: styles.checkboxWrap
  }, /*#__PURE__*/React.createElement("input", _extends({
    className: checkboxClass,
    id: checkboxID,
    ref: checkboxRef // setting the checkbox to indeterminate state, if indeterminate is present in props

  }, rest, {
    type: "checkbox"
  })), /*#__PURE__*/React.createElement("label", {
    className: labelClass,
    htmlFor: checkboxID,
    role: hideLabel ? "none" : null,
    onClick: function onClick(event) {
      return event.stopPropagation();
    } //only need one click event to be invoked and passed back to the user. Expected target of the event is the input.

  }, label ? /*#__PURE__*/React.createElement("span", null, label) : null));
});
Checkbox.propTypes = {
  /**
   * Size of the Checkbox. accepts s, m, l for small, medium & large
   */
  size: PropTypes.oneOf(["s", "m", "l"]),

  /**
   * Label for the Checkbox
   */
  label: PropTypes.string,

  /**
   * Set indeterminate status of checkbox
   */
  indeterminate: PropTypes.bool
};
Checkbox.defaultProps = {
  size: "m",
  label: "",
  indeterminate: false
};

export default Checkbox;
